import React, { useCallback, useEffect, useState } from "react"
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    TableCell,
    TableRow,
    TextField,
    Typography,
} from "@mui/material"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import { handlePromise } from "../../../../utils/handlePromisse"
import {
    enrollParticipants,
    ExperimentDetail,
    getExperimentDetail,
} from "../../../../services/ai-experiment.service"
import { getPaginatedUsers } from "../../../../services/userService"

type UserOption = {
    id: number
    name: string
    email: string
}

type Props = {
    open: boolean
    experimentId: number | null
    onClose: () => void
    onError: (message: string) => void
    onNotify: (message: string) => void
}

export const EnrollDialog: React.FC<Props> = ({ open, experimentId, onClose, onError, onNotify }) => {
    const [detail, setDetail] = useState<ExperimentDetail | null>(null)
    const [loading, setLoading] = useState(false)

    const [searchInput, setSearchInput] = useState("")
    const [userOptions, setUserOptions] = useState<UserOption[]>([])
    const [searchingUsers, setSearchingUsers] = useState(false)
    const [selectedUsers, setSelectedUsers] = useState<UserOption[]>([])
    const [enrolling, setEnrolling] = useState(false)

    const refresh = useCallback(async () => {
        if (!experimentId) return
        setLoading(true)
        const [result, error] = await handlePromise(getExperimentDetail(experimentId))
        setLoading(false)
        if (error) {
            onError(error.response?.data?.message || "Erro ao carregar experimento")
            return
        }
        setDetail(result.data)
    }, [experimentId, onError])

    useEffect(() => {
        if (open && experimentId) {
            refresh()
            setSelectedUsers([])
            setSearchInput("")
            setUserOptions([])
        }
    }, [open, experimentId, refresh])

    useEffect(() => {
        if (!open) return
        const trimmed = searchInput.trim()
        if (trimmed.length < 2) {
            setUserOptions([])
            return
        }
        let cancelled = false
        const timer = setTimeout(async () => {
            setSearchingUsers(true)
            const [result, error] = await handlePromise(
                getPaginatedUsers({ page: 1, size: 10, query: trimmed })
            )
            if (cancelled) return
            setSearchingUsers(false)
            if (error) return
            const items = (result.data?.data ?? []).map((u: any) => ({
                id: u.id ?? u.id_User,
                name: u.name ?? u.name_User ?? "—",
                email: u.email ?? u.email_User ?? "",
            }))
            setUserOptions(items)
        }, 300)
        return () => {
            cancelled = true
            clearTimeout(timer)
        }
    }, [searchInput, open])

    const handleEnroll = async () => {
        if (!experimentId || selectedUsers.length === 0) return
        setEnrolling(true)
        const [result, error] = await handlePromise(
            enrollParticipants(experimentId, selectedUsers.map((u) => u.id))
        )
        setEnrolling(false)
        if (error) {
            onError(error.response?.data?.message || "Erro ao inscrever participantes")
            return
        }
        onNotify(`${result.data.added} inscritos (A=${result.data.cohortA}, B=${result.data.cohortB}). Acesso à Cleo liberado.`)
        setSelectedUsers([])
        setSearchInput("")
        await refresh()
    }

    const participants = detail?.participants ?? []

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                Participantes — {detail?.name ?? ""}
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Inscreva as beta-testers. O sistema balanceia automaticamente entre os grupos A e B,
                    e libera o acesso à Cleo (cleo_enabled) para cada uma. Crossover e início são feitos
                    pela tabela de experimentos.
                </Typography>

                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", mb: 2 }}>
                    <Autocomplete
                        multiple
                        sx={{ flex: 1 }}
                        options={userOptions}
                        value={selectedUsers}
                        onChange={(_, value) => setSelectedUsers(value)}
                        onInputChange={(_, value) => setSearchInput(value)}
                        getOptionLabel={(option) => `${option.name} (${option.email})`}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        loading={searchingUsers}
                        filterSelectedOptions
                        noOptionsText={
                            searchInput.length < 2
                                ? "Digite ao menos 2 caracteres"
                                : searchingUsers
                                ? "Buscando..."
                                : "Nenhum usuário encontrado"
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Buscar usuárias (nome ou email)"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {searchingUsers ? <CircularProgress size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                    <Button
                        variant="contained"
                        onClick={handleEnroll}
                        disabled={selectedUsers.length === 0 || enrolling}
                        sx={{ height: 56 }}
                    >
                        {enrolling ? "Inscrevendo..." : `Inscrever (${selectedUsers.length})`}
                    </Button>
                </Box>

                <Divider sx={{ my: 2 }} />

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            {participants.length} participante(s)
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Usuária</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell align="center">Grupo</TableCell>
                                        <TableCell align="center">Provider atual</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {participants.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center">
                                                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                                    Nenhum participante inscrito
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {participants.map((p) => (
                                        <TableRow hover key={p.userId}>
                                            <TableCell>{p.userName || `#${p.userId}`}</TableCell>
                                            <TableCell>{p.userEmail || "—"}</TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    size="small"
                                                    label={p.cohort}
                                                    color={p.cohort === "A" ? "primary" : "secondary"}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip size="small" variant="outlined" label={p.currentProvider} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    )
}
